import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Film, Upload, Trash2, X, Plus, Target, Wand2 } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Input } from '../components/ui/Input';
import toast from 'react-hot-toast';
import { videosApi } from '../services/api';
import type { Video } from '../types';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default function Videos() {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editGender, setEditGender] = useState<string>('');
  const [editAgeMin, setEditAgeMin] = useState<string>('');
  const [editAgeMax, setEditAgeMax] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { data: videos, isLoading } = useQuery({
    queryKey: ['videos'],
    queryFn: () => videosApi.list(),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) =>
      videosApi.upload(file, (percent) => setUploadProgress(percent)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos'] });
      toast.success('Video uploaded successfully');
      closeUploadModal();
    },
    onError: () => {
      toast.error('Failed to upload video');
      setUploadProgress(null);
    },
  });

  const demographicsMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { target_gender?: string | null; target_age_min?: number | null; target_age_max?: number | null } }) =>
      videosApi.updateDemographics(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos'] });
      toast.success('Targeting updated');
      setEditingId(null);
    },
    onError: () => {
      toast.error('Failed to update targeting');
    },
  });

  const startEditingDemographics = (video: Video) => {
    setEditingId(video.id);
    setEditGender(video.target_gender || '');
    setEditAgeMin(video.target_age_min?.toString() || '');
    setEditAgeMax(video.target_age_max?.toString() || '');
  };

  const saveDemographics = (id: string) => {
    demographicsMutation.mutate({
      id,
      data: {
        target_gender: editGender || null,
        target_age_min: editAgeMin ? parseInt(editAgeMin) : null,
        target_age_max: editAgeMax ? parseInt(editAgeMax) : null,
      },
    });
  };

  const autoDetectMutation = useMutation({
    mutationFn: () => videosApi.autoDetectDemographics(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['videos'] });
      if (result.updated > 0) {
        toast.success(`Auto-detected targeting for ${result.updated} video(s)`);
      } else {
        toast.success('No videos with detectable filenames found');
      }
    },
    onError: () => {
      toast.error('Failed to auto-detect demographics');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => videosApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos'] });
      toast.success('Video deleted');
      setDeleteConfirmId(null);
    },
    onError: () => {
      toast.error('Failed to delete video');
    },
  });

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadProgress(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadMutation.mutate(file);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Videos</h1>
          <p className="text-gray-500 dark:text-slate-400">Manage uploaded video content</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => autoDetectMutation.mutate()} disabled={autoDetectMutation.isPending}>
            <Wand2 className="w-4 h-4 mr-2" />
            {autoDetectMutation.isPending ? 'Detecting...' : 'Auto-Detect Targeting'}
          </Button>
          <Button onClick={() => setShowUploadModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Upload Video
          </Button>
        </div>
      </div>

      {/* Video Grid */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-500 dark:text-slate-400">Loading videos...</div>
      ) : videos && videos.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.map((video: Video) => (
            <Card
              key={video.id}
              className="hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center min-w-0">
                  <div className="p-2 rounded-lg bg-gray-100 dark:bg-slate-700 shrink-0">
                    <Film className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                  </div>
                  <div className="ml-3 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                      {video.filename}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-slate-400">
                      {formatFileSize(video.size_bytes)}
                      {video.duration_seconds !== null && (
                        <span className="ml-2">{formatDuration(video.duration_seconds)}</span>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirmId(video.id);
                  }}
                  className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded shrink-0 ml-2"
                  title="Delete video"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>

              {/* Targeting badges */}
              <div className="mt-3 pt-3 border-t dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 flex-wrap">
                    {video.target_gender ? (
                      <>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                          {video.target_gender}
                        </span>
                        {video.target_age_min != null && video.target_age_max != null && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            {video.target_age_min}-{video.target_age_max}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-slate-500">No targeting</span>
                    )}
                  </div>
                  <button
                    onClick={() => editingId === video.id ? setEditingId(null) : startEditingDemographics(video)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
                    title="Edit targeting"
                  >
                    <Target className="w-3.5 h-3.5 text-gray-400 dark:text-slate-500" />
                  </button>
                </div>

                {/* Inline edit form */}
                {editingId === video.id && (
                  <div className="mt-3 space-y-2">
                    <Select
                      options={[
                        { value: '', label: 'Any gender' },
                        { value: 'male', label: 'Male' },
                        { value: 'female', label: 'Female' },
                      ]}
                      value={editGender}
                      onChange={(e) => setEditGender(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Age min"
                        value={editAgeMin}
                        onChange={(e) => setEditAgeMin(e.target.value)}
                      />
                      <Input
                        type="number"
                        placeholder="Age max"
                        value={editAgeMax}
                        onChange={(e) => setEditAgeMax(e.target.value)}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => saveDemographics(video.id)}
                        isLoading={demographicsMutation.isPending}
                        className="flex-1"
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingId(null)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-2 pt-2 border-t dark:border-slate-700">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-slate-400">Uploaded</span>
                  <span className="text-gray-900 dark:text-white">
                    {new Date(video.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="text-center py-12">
          <Film className="w-12 h-12 mx-auto text-gray-300 dark:text-slate-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No videos yet</h3>
          <p className="text-gray-500 dark:text-slate-400 mb-4">
            Upload your first video to get started
          </p>
          <Button onClick={() => setShowUploadModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Upload Video
          </Button>
        </Card>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Card className="w-full max-w-md mx-4 dark:bg-slate-800 border dark:border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Upload Video
              </h2>
              <button
                onClick={closeUploadModal}
                className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
                disabled={uploadMutation.isPending}
              >
                <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
              </button>
            </div>

            {uploadProgress !== null ? (
              <div className="space-y-4">
                <div className="text-sm text-gray-600 dark:text-slate-300 text-center">
                  Uploading...
                </div>
                <div className="w-full bg-gray-200 dark:bg-slate-600 rounded-full h-3">
                  <div
                    className="bg-primary-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <div className="text-sm text-gray-500 dark:text-slate-400 text-center">
                  {uploadProgress}%
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-8 text-center cursor-pointer hover:border-gray-400 dark:hover:border-slate-500 transition-colors"
                >
                  <Upload className="w-10 h-10 mx-auto text-gray-400 dark:text-slate-500 mb-3" />
                  <p className="text-sm text-gray-600 dark:text-slate-300">
                    Click to select a video file
                  </p>
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                    Supports all common video formats
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <Button
                variant="outline"
                onClick={closeUploadModal}
                disabled={uploadMutation.isPending}
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Card className="w-full max-w-md mx-4 dark:bg-slate-800 border dark:border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-red-600 dark:text-red-400">
                Delete Video
              </h2>
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
              </button>
            </div>

            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800 dark:text-red-300">
                Are you sure you want to delete this video? This action cannot be undone.
              </p>
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => deleteMutation.mutate(deleteConfirmId)}
                isLoading={deleteMutation.isPending}
                className="flex-1"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Video
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
